<!--
  The comment sidebar. TASK-041, first slice.

  There was no comment UI in the browser at all: comments were rows an agent
  could write and read and a person could not see. This lists a document's
  threads grouped by state -- open, handled, closed -- and gives a person the
  acts that are theirs: comment, reply, close, reopen. "Handled" is never
  offered here; it is an agent's report of its own work, and the server refuses
  it from a person.

  It owns its own data and redraws on its own. The document body has a
  single-flight draw with a dirty flag that exists for failures that were
  actually observed (TASK-034); a comment arriving must not tear that down, so
  DocView bumps `refresh` on a `comment` / `comment_state` event and this
  refetches the thread list alone, with the same single-flight shape. It also
  refetches on a `rev`: an edit moves anchors (TASK-030) and can detach them,
  and neither sends a comment event.

  ANCHORED COMMENTS. When the page hands it an `anchor` (a selection in the
  document, turned into the server's `{text, nth}` by lib/anchors.ts), the
  composer shows the quoted words above the text box and posts them with the
  tab's node id; "Remove" makes it a comment on the whole document again. Each
  thread with an anchor tells the page when it is hovered or focused, so the
  page can light up and scroll to its words, and `focusThread` lets the page
  bring a thread forward when its highlight is chosen.

  GAPS (TASK-044). The anchor handed over may instead be a PLACE between two
  blocks (a gap draft): the composer then shows where, as a location rather
  than a quotation, and posts `{kind: 'gap', after, before}` -- never the
  label, which the server computes. A gap thread shows its label; once an
  answer has written into the gap it is a text anchor on the new words, and
  the thread keeps the label (`quote`) with "Answered: the new text is
  highlighted".

  DIAGRAMS (TASK-045). An anchor on a diagram quotes its Mermaid source. That
  is never shown: the page passes the source of every diagram, and a thread
  or draft on one shows the label as it reads, or "The whole diagram", as a
  place (no quote marks) -- like a gap's label.

  MENTIONS. The composer and every reply box are MentionTextareas: `@` offers
  the people with access and their agents. A posted mention renders as a chip,
  and a comment that mentions the viewer (or the viewer's agent, when the
  browser holds an agent's token) is marked "Mentions you".
-->
<script lang="ts">
  import { tick } from 'svelte'
  import { api, describeError } from '../api'
  import type { AnchorDraft } from '../anchors'
  import { diagramShown } from '../diagram'
  import { liveMentions, mentionTitle, segmentBody } from '../mentions'
  import { composerLine } from '../presence'
  import type { CommentRow, MentionCandidate, MentionRef, ThreadState, Viewer } from '../types'
  import MentionTextarea from './MentionTextarea.svelte'

  let {
    docId,
    canComment,
    isAgent = false,
    watching = null,
    refresh = 0,
    nodeId = null,
    me = null,
    anchor = null,
    diagrams = new Map(),
    active = null,
    onclearanchor,
    onrows,
    onactive,
    onshow,
  }: {
    docId: string
    /** The caller's role allows commenting (commenter, editor, owner). */
    canComment: boolean
    /** The browser is signed in with an agent's token (a developer bearer). */
    isAgent?: boolean
    /**
     * Who is on the document (TASK-041 Phase 2), for the composer's honest
     * line: whether an agent is parked on it now. `null` when not known.
     */
    watching?: Viewer[] | null
    /** Bumped by the page on every comment event, every `rev`, and every reconnect. */
    refresh?: number
    /** The tab's content node: an anchor is resolved against it. */
    nodeId?: string | null
    /** Who is reading, to mark the comments that mention them. */
    me?: { userId: string; agentId: string | null } | null
    /** Words selected in the document to comment on, or null for the whole document. */
    anchor?: AnchorDraft | null
    /** Every diagram's Mermaid source, by its block id (TASK-045). */
    diagrams?: Map<string, string>
    /** The thread whose words are lit up in the document. */
    active?: string | null
    onclearanchor?: () => void
    /** Every loaded list of rows, for the page to draw highlights from. */
    onrows?: (rows: CommentRow[]) => void
    /** A thread was hovered or focused (id), or left (null). */
    onactive?: (threadId: string | null, how: 'hover' | 'focus') => void
    /** Show this thread's words in the document. */
    onshow?: (threadId: string) => void
  } = $props()

  interface Thread {
    root: CommentRow
    replies: CommentRow[]
    state: ThreadState
  }

  let rows = $state<CommentRow[]>([])
  let candidates = $state<MentionCandidate[]>([])
  let loaded = $state(false)
  let loadError = $state<string | null>(null)
  let actionError = $state<string | null>(null)
  let notice = $state<string | null>(null)

  let draft = $state('')
  let draftMentions = $state<MentionRef[]>([])
  let draftEl = $state<HTMLTextAreaElement | null>(null)
  let posting = $state(false)
  let replyOpen = $state<Record<string, boolean>>({})
  let replyDraft = $state<Record<string, string>>({})
  let replyMentions = $state<Record<string, MentionRef[]>>({})
  let busy = $state<Record<string, boolean>>({})
  let closedOpen = $state(false)

  let asideEl = $state<HTMLElement | null>(null)

  const threads = $derived.by(() => {
    const byRoot = new Map<string, Thread>()
    for (const row of rows) {
      if (row.parent_utterance_id === null && row.kind === 'comment') {
        byRoot.set(row.id, { root: row, replies: [], state: row.state ?? 'open' })
      }
    }
    for (const row of rows) {
      if (row.parent_utterance_id === null) continue
      byRoot.get(row.thread_id)?.replies.push(row)
    }
    return [...byRoot.values()]
  })

  const groups = $derived({
    open: threads.filter((t) => t.state === 'open'),
    handled: threads.filter((t) => t.state === 'handled'),
    closed: threads.filter((t) => t.state === 'closed'),
  })

  // An agent by its name AND its person: out of the chip, "Claude" alone would
  // not say whose.
  const nameOf = $derived(new Map(candidates.map((c) => [c.id, mentionTitle(c) ?? c.name])))

  /* One fetch at a time; anything that arrives meanwhile runs once more after. */
  let inFlight: Promise<void> | null = null
  let dirty = false

  async function loadOnce(id: string) {
    const res = await api<{ rows: CommentRow[] }>(`/v1/docs/${id}/comments`)
    if (id !== docId) return
    rows = res.rows ?? []
    loadError = null
    loaded = true
    onrows?.(rows)
  }

  function load(): Promise<void> {
    if (inFlight) {
      dirty = true
      return inFlight
    }
    const id = docId
    inFlight = (async () => {
      try {
        do {
          dirty = false
          await loadOnce(id)
        } while (dirty)
      } catch (err) {
        loadError = `Comments could not be loaded: ${describeError(err)}.`
      } finally {
        inFlight = null
      }
    })()
    return inFlight
  }

  $effect(() => {
    // Read so the effect re-runs on every bump and on a different document.
    void refresh
    void docId
    load()
  })

  $effect(() => {
    const id = docId
    if (!canComment) {
      candidates = []
      return
    }
    api<{ candidates: MentionCandidate[] }>(`/v1/docs/${id}/comment-targets`)
      .then((res) => {
        if (id === docId) candidates = res.candidates ?? []
      })
      .catch(() => {
        // The list is a convenience: without it a comment simply mentions
        // nobody, and every agent on the document still reads it.
        candidates = []
      })
  })

  // A selection handed over from the document: bring the composer forward.
  $effect(() => {
    if (!anchor || !draftEl) return
    const el = draftEl
    void tick().then(() => {
      el.scrollIntoView({ block: 'nearest' })
      el.focus()
    })
  })

  /** Bring a thread forward: open its group, scroll to it, focus it. */
  export async function focusThread(id: string) {
    if (groups.closed.some((t) => t.root.id === id)) closedOpen = true
    await tick()
    const card = asideEl?.querySelector<HTMLElement>(`[data-thread-id="${CSS.escape(id)}"]`)
    if (!card) return
    /*
     * To the TOP of the pane, not merely into view: a chosen thread is what
     * the person is now reading, and `nearest` left it wherever it happened to
     * sit -- often a line or two above the fold with the composer still taking
     * the top third. Scrolling the pane itself (it is the sticky scroller on a
     * wide window) takes the composer off the top and gives the thread the
     * whole height below it. When the pane is not its own scroller -- a narrow
     * window, where the page scrolls instead -- `block: 'start'` does the same
     * thing to the page.
     */
    toTop(card)
    card.focus({ preventScroll: true })
  }

  /**
   * Changed your mind after selecting: drop the quote AND the text, and clear
   * the selection in the document so its floating control goes away too.
   * `onclearanchor` alone is the other thing -- "comment on the whole document
   * instead" -- which keeps what you typed.
   */
  function cancelDraft() {
    draft = ''
    draftMentions = []
    onclearanchor?.()
    try {
      window.getSelection()?.removeAllRanges()
    } catch {
      /* Safari can throw on a detached selection; there is nothing to undo. */
    }
  }

  /** Put a thread at the TOP of whatever is scrolling: see `focusThread`. */
  function toTop(card: HTMLElement) {
    const scroller = asideEl && asideEl.scrollHeight > asideEl.clientHeight + 1 ? asideEl : null
    if (scroller) {
      const top = card.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
      scroller.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' })
    } else {
      card.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }

  function mailNote(out: { withheld?: string[] }): string | null {
    const names = (out.withheld ?? []).map((id) => nameOf.get(id) ?? 'someone')
    if (!names.length) return null
    return `Posted. ${names.join(', ')} ${names.length === 1 ? 'was' : 'were'} not emailed about it -- too many mention emails just now, or the mail could not be sent.`
  }

  async function post() {
    const body = draft.trim()
    if (!body || posting) return
    posting = true
    actionError = null
    notice = null
    const mentions = liveMentions(body, draftMentions).map(({ kind, id }) => ({ kind, id }))
    const anchored = anchor && nodeId ? anchor : null
    // A gap's label is for display only: the server labels it from the blocks.
    const spec = !anchored
      ? null
      : anchored.kind === 'gap'
        ? { kind: 'gap', after: anchored.after, before: anchored.before }
        : { kind: 'find', text: anchored.text, nth: anchored.nth }
    try {
      const out = await api<{ withheld?: string[] }>(`/v1/docs/${docId}/comments`, {
        method: 'POST',
        body: JSON.stringify({
          body,
          ...(mentions.length ? { mentions } : {}),
          ...(spec ? { nodeId, anchor: spec } : {}),
        }),
      })
      draft = ''
      draftMentions = []
      if (anchored) onclearanchor?.()
      notice = mailNote(out)
      await load()
    } catch (err) {
      actionError = !anchored
        ? `Your comment was not posted: ${describeError(err)}.`
        : anchored.kind === 'gap'
          ? `Your comment was not posted: ${describeError(err)}. If the document changed, choose the place again.`
          : `Your comment was not posted: ${describeError(err)}. If the document changed, select the words again.`
    } finally {
      posting = false
    }
  }

  async function reply(thread: Thread) {
    const id = thread.root.id
    const body = (replyDraft[id] ?? '').trim()
    if (!body || busy[id]) return
    busy[id] = true
    actionError = null
    notice = null
    const mentions = liveMentions(body, replyMentions[id] ?? []).map(({ kind, id }) => ({ kind, id }))
    try {
      const out = await api<{ withheld?: string[] }>(`/v1/docs/${docId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body, parentId: id, ...(mentions.length ? { mentions } : {}) }),
      })
      replyDraft[id] = ''
      replyMentions[id] = []
      replyOpen[id] = false
      notice = mailNote(out)
      await load()
    } catch (err) {
      actionError = `Your reply was not posted: ${describeError(err)}.`
    } finally {
      busy[id] = false
    }
  }

  async function setState(thread: Thread, state: 'closed' | 'open') {
    const id = thread.root.id
    if (busy[id]) return
    busy[id] = true
    actionError = null
    try {
      await api(`/v1/comments/${id}/state`, { method: 'POST', body: JSON.stringify({ state }) })
      await load()
    } catch (err) {
      actionError = `That did not work: ${describeError(err)}.`
    } finally {
      busy[id] = false
    }
  }

  function ago(iso: string | null): string {
    if (!iso) return ''
    const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
    if (seconds < 45) return 'just now'
    const minutes = Math.round(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return new Date(iso).toLocaleDateString()
  }

  function who(row: CommentRow): string {
    const name = row.author ?? 'Someone'
    return row.author_kind === 'agent' ? `${name}'s agent` : name
  }

  function stateLine(row: CommentRow): string {
    const verb =
      row.transition === 'handled' ? 'marked this handled' : row.transition === 'closed' ? 'closed this' : 'reopened this'
    return `${who(row)} ${verb}`
  }

  /** A pre-mention row addressed through the old `to` picker. */
  function addressee(row: CommentRow): string | null {
    if (!row.to || row.mentions?.length) return null
    return nameOf.get(row.to) ?? 'an agent'
  }

  function isMe(m: MentionRef): boolean {
    if (!me) return false
    return m.kind === 'agent' ? m.id === me.agentId : !me.agentId && m.id === me.userId
  }

  function mentionsMe(row: CommentRow): boolean {
    if (!me) return false
    if (row.mentions?.some(isMe)) return true
    return !!me.agentId && row.to === me.agentId
  }

  function hasLiveAnchor(row: CommentRow): boolean {
    return !!row.anchor && (row.anchor.kind === 'gap' || !!row.anchor.text) && !row.detached_at
  }

  /*
   * A PLACE, not words (TASK-044): a gap thread, or one answered into its gap,
   * whose `quote` is the gap's label. Shown as a location -- no quote marks.
   */
  function isPlace(row: CommentRow): boolean {
    return row.anchor?.kind === 'gap' || !!row.anchor?.gap || onDiagram(row)
  }

  /** Anchored to a diagram's source (TASK-045): shown by its label, never as Mermaid. */
  function onDiagram(row: CommentRow): boolean {
    return !row.anchor?.gap && !!row.anchor?.blockId && diagrams.has(row.anchor.blockId)
  }

  /** A diagram label as it reads, or the whole diagram. */
  function diagramPlace(label: string | null): string {
    return label ?? 'The whole diagram'
  }

  /** "Between …" as the end of a sentence: "Comment between …". */
  function spoken(label: string): string {
    return label ? label[0].toLowerCase() + label.slice(1) : label
  }

  function cardLabel(row: CommentRow): string {
    if (!row.anchor?.text) return 'Comment on the document'
    if (onDiagram(row)) {
      const label = diagramShown(quoteOf(row), diagrams.get(row.anchor.blockId!))
      return label ? `Comment on the diagram label “${label}”` : 'Comment on the diagram'
    }
    return isPlace(row) ? `Comment ${spoken(quoteOf(row))}` : `Comment on “${quoteOf(row)}”`
  }

  /*
   * The words as they read when the comment was made. An answer that rewrote
   * them moves the highlight onto the new words but keeps the old ones here, so
   * the thread and the document show the before and after side by side.
   */
  function quoteOf(row: CommentRow): string {
    return row.anchor?.quote ?? row.anchor?.text ?? ''
  }

  /** What the card quotes: the words, or on a diagram its label as it reads. */
  function shownQuote(row: CommentRow): string {
    if (onDiagram(row)) return diagramPlace(diagramShown(quoteOf(row), diagrams.get(row.anchor!.blockId!)))
    return isPlace(row) ? quoteOf(row) : `“${quoteOf(row)}”`
  }

  function rewritten(row: CommentRow): boolean {
    return !row.anchor?.gap && !!row.anchor?.quote && row.anchor.quote !== row.anchor.text
  }

  /** A gap thread answered by writing into its gap: the new text is highlighted. */
  function answeredInto(row: CommentRow): boolean {
    return !!row.anchor?.gap && row.anchor.kind !== 'gap'
  }
</script>

{#snippet body(row: CommentRow)}
  <p class="body">{#each segmentBody(row.body, row.mentions) as piece, i (i)}{#if piece.kind === 'mention'}<span
          class="mention-chip"
          class:me={isMe(piece.mention)}
          data-kind={piece.mention.kind}
          title={mentionTitle(piece.mention)}>{piece.text}</span
        >{:else}{piece.text}{/if}{/each}</p>
{/snippet}

{#snippet threadCard(thread: Thread)}
  {@const root = thread.root}
  {@const id = root.id}
  {@const live = hasLiveAnchor(root)}
  <!-- tabindex -1: the page moves focus here when a highlight is chosen. The
       card itself is not a stop in the tab order; its controls are. -->
  <article
    class="thread"
    class:active={active === id}
    class:mentions-me={mentionsMe(root)}
    data-state={thread.state}
    data-thread-id={id}
    tabindex="-1"
    aria-label={cardLabel(root)}
    onmouseenter={() => live && onactive?.(id, 'hover')}
    onmouseleave={() => live && onactive?.(null, 'hover')}
    onfocusin={(e) => {
      live && onactive?.(id, 'focus')
      /*
       * Choosing a thread IN THE PANE does what choosing its words in the
       * document does: it comes to the top, the composer scrolls off, and the
       * whole height below belongs to the conversation. The card carries
       * tabindex -1, so a click on it lands here as the target; a click on
       * Reply or Close targets the control and is left alone -- scrolling
       * under the hand that is mid-gesture is its own small cruelty.
       */
      if (e.target === e.currentTarget) toTop(e.currentTarget as HTMLElement)
    }}
    onfocusout={(e) => {
      if (live && !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) onactive?.(null, 'focus')
    }}
  >
    {#if root.anchor?.text}
      {#if root.detached_at && root.anchor.kind === 'gap'}
        <div class="quote detached place">
          <span class="quote-text">{quoteOf(root)}</span>
          <span class="tag">The place it pointed to was removed</span>
        </div>
      {:else if root.detached_at}
        <blockquote class="quote detached" class:place={isPlace(root)}>
          <span class="quote-text">{shownQuote(root)}</span>
          <span class="tag">Text removed from the document</span>
        </blockquote>
      {:else}
        <button type="button" class="quote quote-link" class:place={isPlace(root)} onclick={() => onshow?.(id)}>
          <span class="quote-text">{shownQuote(root)}</span>
          {#if answeredInto(root)}<span class="tag">Answered: the new text is highlighted</span>
          {:else if rewritten(root)}<span class="tag">Since rewritten; the new words are highlighted</span>{/if}
          <span class="visually-hidden">Show in the document</span>
        </button>
      {/if}
    {/if}

    <div class="message">
      <div class="meta">
        <span class="author">{who(root)}</span>
        {#if addressee(root)}<span class="to">to {addressee(root)}</span>{/if}
        {#if mentionsMe(root)}<span class="for-you">Mentions you</span>{/if}
        <span class="when">{ago(root.created_at)}</span>
      </div>
      {@render body(root)}
    </div>

    {#each thread.replies as row (row.id)}
      {#if row.kind === 'state'}
        <p class="state-line" data-transition={row.transition}>{stateLine(row)} · {ago(row.created_at)}</p>
      {:else}
        <div class="message reply" class:mentions-me={mentionsMe(row)}>
          <div class="meta">
            <span class="author">{who(row)}</span>
            {#if mentionsMe(row)}<span class="for-you">Mentions you</span>{/if}
            <span class="when">{ago(row.created_at)}</span>
          </div>
          {@render body(row)}
        </div>
      {/if}
    {/each}

    {#if thread.state === 'handled' && canComment && !isAgent}
      <!-- Who handled it and when is the state line above; this is only what
           the person can do about it. -->
      <p class="handled-note">Back with you: close it if you are done, or reply if not.</p>
    {/if}

    {#if canComment}
      {#if replyOpen[id]}
        <div class="reply-box">
          <MentionTextarea
            id={`reply-${id}`}
            label="Reply"
            rows={2}
            placeholder={thread.state === 'closed' ? 'Replying does not reopen a closed thread' : 'Reply, or @ to mention'}
            {candidates}
            bind:value={replyDraft[id]}
            bind:picked={replyMentions[id]}
            onsubmit={() => reply(thread)}
          />
          <div class="actions">
            <button
              type="button"
              class="btn btn-primary btn-small"
              disabled={busy[id] || !(replyDraft[id] ?? '').trim()}
              onclick={() => reply(thread)}>Reply</button
            >
            <button type="button" class="btn btn-quiet btn-small" onclick={() => (replyOpen[id] = false)}>
              Cancel
            </button>
          </div>
        </div>
      {:else}
        <div class="actions">
          <button
            type="button"
            class="btn btn-quiet btn-small"
            onclick={() => {
              replyDraft[id] ??= ''
              replyMentions[id] ??= []
              replyOpen[id] = true
            }}>Reply</button
          >
          {#if !isAgent}
            {#if thread.state === 'closed'}
              <button
                type="button"
                class="btn btn-quiet btn-small"
                disabled={busy[id]}
                onclick={() => setState(thread, 'open')}>Reopen</button
              >
            {:else}
              <button
                type="button"
                class="btn btn-quiet btn-small"
                disabled={busy[id]}
                onclick={() => setState(thread, 'closed')}>Close</button
              >
            {/if}
          {/if}
        </div>
      {/if}
    {/if}
  </article>
{/snippet}

<aside class="comments" aria-label="Comments" bind:this={asideEl}>
  <h2 class="heading">Comments</h2>

  {#if canComment}
    <div class="composer">
      {#if anchor?.kind === 'gap'}
        <div class="composer-quote">
          <span class="composer-quote-label" id="composer-quote-label">Commenting on a place in the document</span>
          <p class="quote place" aria-labelledby="composer-quote-label">{anchor.label}</p>
          <button type="button" class="btn btn-quiet btn-small" onclick={() => onclearanchor?.()}>
            Comment on the whole document instead
          </button>
        </div>
      {:else if anchor?.diagram}
        <div class="composer-quote">
          <span class="composer-quote-label" id="composer-quote-label">Commenting on a diagram</span>
          <p class="quote place" aria-labelledby="composer-quote-label">{diagramPlace(anchor.diagram.label)}</p>
          <button type="button" class="btn btn-quiet btn-small" onclick={() => onclearanchor?.()}>
            Comment on the whole document instead
          </button>
        </div>
      {:else if anchor}
        <div class="composer-quote">
          <span class="composer-quote-label" id="composer-quote-label">Commenting on</span>
          <blockquote class="quote" aria-labelledby="composer-quote-label">“{anchor.text}”</blockquote>
          {#if anchor.clipped}
            <p class="hint">Only the first paragraph of your selection is quoted: a comment attaches to one paragraph.</p>
          {/if}
          <button type="button" class="btn btn-quiet btn-small" onclick={() => onclearanchor?.()}>
            Comment on the whole document instead
          </button>
        </div>
      {/if}
      <MentionTextarea
        id="comment-draft"
        label={!anchor
          ? 'Add a comment'
          : anchor.kind === 'gap'
            ? `Comment ${spoken(anchor.label)}`
            : anchor.diagram
              ? anchor.diagram.label
                ? `Comment on the diagram label “${anchor.diagram.label}”`
                : 'Comment on the diagram'
              : `Comment on “${anchor.text}”`}
        placeholder={!anchor
          ? 'Add a comment, or @ to mention'
          : anchor.kind === 'gap'
            ? 'Say what belongs here, or @ to mention'
            : anchor.diagram
              ? 'Comment on the diagram, or @ to mention'
            : 'Comment on the selected text, or @ to mention'}
        {candidates}
        bind:value={draft}
        bind:picked={draftMentions}
        bind:element={draftEl}
        onsubmit={post}
      />
      <div class="actions">
        <button type="button" class="btn btn-primary btn-small" disabled={posting || !draft.trim()} onclick={post}>
          Comment
        </button>
        {#if anchor || draft.trim()}
          <button type="button" class="btn btn-quiet btn-small" disabled={posting} onclick={cancelDraft}>
            Cancel
          </button>
        {/if}
      </div>
      <p class="hint">
        Select words in the document to comment on them, or point between two paragraphs to ask for something
        there; @ mentions someone, and emails a person.
      </p>
      <!--
        §8's honest line: "watching" means an agent has a wait parked on this
        document right now, so a comment reaches it within seconds. When none
        has, the comment is still kept -- it is a row -- and read next time.
      -->
      <p class="hint watching-line" class:watched={(watching ?? []).some((v) => v.kind === 'agent')}>
        {composerLine(watching)}
      </p>
    </div>
  {/if}

  {#if notice}<p class="notice" role="status">{notice}</p>{/if}
  {#if actionError}<p class="error" role="alert">{actionError}</p>{/if}
  {#if loadError}<p class="error">{loadError}</p>{/if}

  {#if loaded && threads.length === 0}
    <p class="none">No comments yet.</p>
  {/if}

  {#if groups.open.length}
    <section class="group" aria-label="Open threads">
      <h3 class="group-title">Open <span class="count">{groups.open.length}</span></h3>
      {#each groups.open as thread (thread.root.id)}{@render threadCard(thread)}{/each}
    </section>
  {/if}

  {#if groups.handled.length}
    <section class="group" aria-label="Handled threads">
      <h3 class="group-title">Handled, waiting on you <span class="count">{groups.handled.length}</span></h3>
      {#each groups.handled as thread (thread.root.id)}{@render threadCard(thread)}{/each}
    </section>
  {/if}

  {#if groups.closed.length}
    <details class="group" bind:open={closedOpen}>
      <summary class="group-title">Closed <span class="count">{groups.closed.length}</span></summary>
      {#each groups.closed as thread (thread.root.id)}{@render threadCard(thread)}{/each}
    </details>
  {/if}
</aside>

<style>
  .comments {
    font-size: 0.9375rem;
    line-height: 1.5;
  }

  /*
   * Beside the document, the sidebar keeps its place while the document
   * scrolls, and scrolls on its own. That is what lets hovering a thread
   * scroll its words into view without scrolling the thread out from under the
   * pointer. Stacked (narrow), it is simply the rest of the page.
   */
  @media (min-width: 64rem) {
    .comments {
      position: sticky;
      top: 5.5rem;
      max-height: calc(100dvh - 6.5rem);
      overflow-y: auto;
      overscroll-behavior: contain;
      /* Room for focus outlines, which overflow clipping would cut. */
      padding: 2px 4px 2px 2px;
    }
  }

  .heading {
    font-size: var(--text-meta);
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-ink-soft);
    margin: 0 0 1rem;
  }

  .composer,
  .reply-box {
    display: grid;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }

  .reply-box {
    margin: 0.75rem 0 0;
  }

  .composer-quote {
    display: grid;
    gap: 0.375rem;
    justify-items: start;
  }

  .composer-quote-label {
    font-size: var(--text-meta);
    color: var(--color-ink-soft);
  }

  .composer-quote .quote {
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    background: var(--anchor-tint);
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .composer .actions,
  .reply-box .actions {
    margin-top: 0;
  }

  .notice {
    color: var(--color-ink-soft);
    font-size: var(--text-meta);
    margin: 0 0 1rem;
  }

  .hint,
  .none {
    color: var(--color-ink-soft);
    font-size: var(--text-meta);
    margin: 0;
  }

  /* An agent is parked on the doc: said in the accent, as the strip says it. */
  .watching-line.watched {
    color: var(--color-red-deep);
  }

  .error {
    color: var(--color-red-deep);
    font-size: var(--text-meta);
    margin: 0 0 1rem;
  }

  .group {
    margin-bottom: 1.5rem;
  }

  .group-title {
    font-size: var(--text-meta);
    font-weight: 600;
    color: var(--color-ink-soft);
    margin: 0 0 0.75rem;
  }

  summary.group-title {
    cursor: pointer;
  }

  .count {
    font-weight: 400;
  }

  .thread {
    border: 1px solid var(--color-line);
    border-radius: 6px;
    padding: 0.875rem 1rem;
    margin-bottom: 0.75rem;
    background: var(--paper);
  }

  .thread[data-state='handled'] {
    border-left: 2px solid var(--color-red-deep);
  }

  .thread[data-state='closed'] {
    opacity: 0.75;
  }

  .quote {
    margin: 0 0 0.625rem;
    padding-left: 0.625rem;
    border-left: 2px solid var(--color-line);
    color: var(--color-ink-soft);
    font-size: var(--text-meta);
  }

  /* The quote of a live anchor is a control: it shows the words in the
     document. Drawn as the quote it is, with the highlight's warm tint. */
  .quote-link {
    display: block;
    width: 100%;
    text-align: left;
    font: inherit;
    font-size: var(--text-meta);
    color: var(--color-ink-soft);
    background: none;
    border: none;
    border-left: 2px solid var(--anchor-edge);
    border-radius: 0;
    padding: 0 0 0 0.625rem;
    cursor: pointer;
  }

  .quote-link:hover .quote-text {
    background: var(--anchor-tint);
    color: var(--ink);
  }

  .quote.detached .quote-text {
    text-decoration: line-through;
  }

  /* A place, not a quotation (no quote marks): a removed place is not struck
     through -- it was never words. */
  .quote.detached.place .quote-text {
    text-decoration: none;
  }

  .thread.active {
    border-color: var(--anchor-edge);
    box-shadow: 0 0 0 1px var(--anchor-edge);
  }

  .thread.active .quote-text {
    background: var(--anchor-tint-strong);
    color: var(--ink);
  }

  .thread:focus-visible {
    outline: 2px solid var(--color-red-deep);
    outline-offset: 2px;
  }

  .for-you {
    color: var(--color-red-deep);
    font-weight: 600;
  }

  .mention-chip {
    font-weight: 600;
    color: var(--ink);
    background: var(--color-plinth);
    border: 1px solid var(--color-line);
    border-radius: 3px;
    padding: 0 0.25em;
    white-space: nowrap;
  }

  /* The viewer's own name: the one chip in red, and underlined as well, so it
     is not told apart by colour alone. */
  .mention-chip.me {
    color: var(--color-red-deep);
    border-color: color-mix(in oklab, var(--color-red-deep) 45%, transparent);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .reply.mentions-me {
    border-left-color: var(--color-red-deep);
  }

  .tag {
    display: block;
    margin-top: 0.25rem;
    font-style: italic;
  }

  .message + .message,
  .state-line {
    margin-top: 0.625rem;
  }

  .reply {
    padding-left: 0.75rem;
    border-left: 1px solid var(--color-line);
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    font-size: var(--text-meta);
    color: var(--color-ink-soft);
  }

  .author {
    color: var(--ink);
    font-weight: 600;
  }

  .body {
    margin: 0.125rem 0 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .state-line,
  .handled-note {
    font-size: var(--text-meta);
    color: var(--color-ink-soft);
    font-style: italic;
    margin-bottom: 0;
  }

  .handled-note {
    margin-top: 0.625rem;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
</style>
